'use client';

import { useToasts, type Toast, type ToastVariant } from './use-toast';

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; icon: string }> = {
  success: {
    bg: 'bg-[var(--k-color-success-soft)]',
    border: 'border-[var(--k-color-success)]/30',
    icon: '✓',
  },
  error: {
    bg: 'bg-[var(--k-color-danger-soft)]',
    border: 'border-[var(--k-color-danger)]/30',
    icon: '⚠',
  },
  info: {
    bg: 'bg-[var(--k-color-accent-soft)]',
    border: 'border-[var(--k-color-accent)]/30',
    icon: 'i',
  },
  warning: {
    bg: 'bg-[var(--k-color-warning-soft)]',
    border: 'border-[var(--k-color-warning)]/30',
    icon: '!',
  },
};

export function ToastContainer() {
  const { toasts, dismiss } = useToasts();
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-4 right-4 z-[70] flex flex-col gap-2 sm:bottom-6 sm:right-6"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const style = VARIANT_STYLES[toast.variant];
  return (
    <div
      role="alert"
      data-tour={`toast-${toast.variant}`}
      className={`pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-[var(--k-radius-md)] border ${style.bg} ${style.border} p-3 shadow-[var(--k-shadow-md)]`}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--k-color-surface)] text-xs font-bold"
      >
        {style.icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[var(--k-color-text-primary)]">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-xs text-[var(--k-color-text-secondary)]">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Schließen"
        className="shrink-0 rounded-md p-1 text-[var(--k-color-text-muted)] transition hover:bg-[var(--k-color-surface)] hover:text-[var(--k-color-text-primary)]"
      >
        ×
      </button>
    </div>
  );
}